// Client-side risk signals collected in the shopper's browser on the hosted checkout page and sent
// with the /pay request, where the backend risk gate scores them (RiskRuleType.DEVICE_BOT, and a
// device fingerprint kept for future device-velocity rules).
//
// Both are entirely best-effort: if a library fails to load, times out, or throws, we return an
// empty object and the payment proceeds exactly as before. Nothing here can block a checkout.
//
//   - botDetected       BotD's verdict that the browser is an automated client (headless, webdriver,
//                       spoofed environment, etc.).
//   - deviceFingerprint ThumbmarkJS's stable per-device hash. Computed anonymously (no API key), so
//                       it makes no network call and no data leaves the browser except in our own
//                       /pay request.

export interface DeviceSignals {
  deviceFingerprint?: string;
  botDetected?: boolean;
  /** MOBILE / DESKTOP / TABLET, computed from Client Hints + primary pointer + screen size. */
  deviceType?: string;
}

/**
 * Robust client-side device classification — more reliable than parsing the User-Agent, and it
 * corrects the two cases UA sniffing gets wrong:
 *   - a phone with "request desktop site" on (sends a desktop UA, but is still touch-first + small),
 *   - iPadOS Safari's default "desktop" mode (reports as Macintosh, but is touch-first + tablet-sized).
 *
 * Signals used: Client Hints `navigator.userAgentData.mobile` (a clean mobile flag on Chromium), the
 * primary pointer (`pointer: coarse` = the device's main input is a finger, which a trackpad laptop
 * reports as `fine` even if it has a touchscreen), and the screen's shorter side to split phone vs
 * tablet. Returns undefined only if the environment is too locked-down to read any of it.
 */
function detectDeviceType(): string | undefined {
  try {
    const uaData = (navigator as any).userAgentData;
    const mobileHint: boolean | undefined =
      uaData && typeof uaData.mobile === 'boolean' ? uaData.mobile : undefined;

    const coarsePointer =
      typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

    const shortSide = Math.min(
      window.screen?.width ?? 0,
      window.screen?.height ?? 0,
    );
    // Phones' shorter (logical) side is ~430px or less; tablets start around 600px+.
    const tabletSized = shortSide >= 600;

    // Client Hints is the strongest signal when present.
    if (mobileHint === true) return tabletSized ? 'TABLET' : 'MOBILE';

    // Touch-first device (finger is the primary pointer) → phone or tablet by size. This is what
    // catches iPad-as-Mac (no Client Hints, coarse pointer, tablet-sized) and desktop-mode phones
    // (coarse pointer, small screen), while a touch laptop (trackpad = fine primary pointer) is not
    // caught here and correctly falls through to desktop below.
    if (coarsePointer) return tabletSized ? 'TABLET' : 'MOBILE';

    // Fine primary pointer (mouse/trackpad) → desktop or laptop, regardless of what the UA claims.
    return 'DESKTOP';
  } catch {
    return undefined;
  }
}

let cached: DeviceSignals | null = null;
let inFlight: Promise<DeviceSignals> | null = null;

async function detectBot(): Promise<boolean | undefined> {
  try {
    const { load } = await import('@fingerprintjs/botd');
    const botd = await load();
    const result = botd.detect();
    return result.bot === true;
  } catch {
    return undefined;
  }
}

async function computeFingerprint(): Promise<string | undefined> {
  try {
    const { getThumbmark } = await import('@thumbmarkjs/thumbmarkjs');
    const { thumbmark } = await getThumbmark();
    return thumbmark || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Collects the device signals once and caches the result for the life of the page, so repeated pay
 * attempts don't recompute. Never rejects — resolves to {} if everything failed.
 */
export async function collectDeviceSignals(): Promise<DeviceSignals> {
  if (cached) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const [botDetected, deviceFingerprint] = await Promise.all([detectBot(), computeFingerprint()]);
    const signals: DeviceSignals = {};
    if (typeof botDetected === 'boolean') signals.botDetected = botDetected;
    if (deviceFingerprint) signals.deviceFingerprint = deviceFingerprint;
    const deviceType = detectDeviceType();
    if (deviceType) signals.deviceType = deviceType;
    cached = signals;
    return signals;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}
